import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  AuthenticationResult,
  InteractionRequiredAuthError,
  IPublicClientApplication,
} from "@azure/msal-browser";
import { useConfig } from "./ConfigContext";
import { useAppInsights } from "./AppInsightsContext";

export enum SessionState {
  Pending,
  Busy,
  Settled,
}

type MSALAuth = {
  instance: IPublicClientApplication;
  auth?: AuthenticationResult;
  session: {
    state: SessionState;
    logIn(): void;
    logOut(): void;
  };
};

let redirectHandleCount = 0;

const Context = createContext<MSALAuth | null>(null);

interface MSALContextProps {
  children: React.ReactNode;
  instance: IPublicClientApplication;
}

export function MSALContext({ children, instance }: MSALContextProps) {
  const config = useConfig();
  const appInsights = useAppInsights();

  const [state, setState] = useState(SessionState.Pending);

  const [auth, setAuth] = useState<AuthenticationResult>();

  const logIn = useCallback(() => {
    setState(SessionState.Busy);
    const request = { scopes: config.msal.scopes.token };
    instance.loginRedirect(request);
  }, [instance, config]);

  const logOut = useCallback(() => {
    setState(SessionState.Busy);
    instance.logoutRedirect();
  }, [instance]);

  useEffect(() => {
    if (redirectHandleCount > 0) {
      return;
    }
    redirectHandleCount++;

    setState(SessionState.Busy);

    instance
      .handleRedirectPromise()
      .then((auth) => {
        if (auth) {
          console.debug("Auth from Redirect", auth);
          return auth;
        }

        const request = { scopes: config.msal.scopes.token };
        return instance.ssoSilent(request).then((auth) => {
          console.debug("Auth from Silent", auth);
          return auth;
        });
      })
      .then((auth) => {
        if (auth) {
          setAuth(auth);
        } else {
          console.debug("No auth found");
        }
      })
      .catch((error) => {
        if (error instanceof InteractionRequiredAuthError) {
          console.debug("Silent sign in attempt not possible", error);
          return;
        }
        console.error("MSAL Error", error);
      })
      .finally(() => {
        setState(SessionState.Settled);
      });
  }, [config, instance]);

  useEffect(() => {
    if (!auth) {
      return;
    }

    appInsights.setAuthenticatedUserContext(
      `${auth.tenantId}:${auth.uniqueId}`
    );

    return () => {
      appInsights.clearAuthenticatedUserContext();
    };
  }, [auth, appInsights]);

  const session = {
    state,
    logOut,
    logIn,
  };

  return (
    <Context.Provider value={{ instance, auth, session }}>
      {children}
    </Context.Provider>
  );
}

export function useMSAL() {
  const context = useContext(Context);
  if (!context) {
    throw new Error("useMSAL without MSALContext");
  }
  return context;
}
